import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QRCodeModal from './QRCodeModal';
import AnalyticsModal from './AnalyticsModal';
import { qrCodeService } from '../../../utils/storage';
import { useToast } from '../../../hooks/useToast';

export default function QRCodeList() {
  const { t } = useTranslation('dashboard');
  const { showToast, ToastContainer } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [qrCodes, setQrCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyticsModal, setAnalyticsModal] = useState<{ isOpen: boolean; qrCode: QRCode | null }>({
    isOpen: false,
    qrCode: null,
  });

  const loadQRCodes = async () => {
    try {
      setLoading(true);
      const data = await qrCodeService.getAll();
      setQrCodes(data);
    } catch (error) {
      console.error('QR 코드 로드 실패:', error);
      showToast('QR 코드를 불러오는데 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQRCodes();
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    try {
      await qrCodeService.delete(id);
      showToast('QR 코드가 삭제되었습니다', 'success');
      loadQRCodes();
    } catch (error) {
      console.error('QR 코드 삭제 실패:', error);
      showToast('QR 코드 삭제에 실패했습니다', 'error');
    }
  };

  const handleDownload = (qrImage: string, name: string) => {
    const link = document.createElement('a');
    link.href = qrImage;
    link.download = `${name}.png`;
    link.click();
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
        <p className="text-gray-600 mt-4">로딩 중...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">QR 코드 목록</h3>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i>
              {t('qr.create')}
            </button>
          </div>
        </div>

        {qrCodes.length === 0 ? (
          <div className="p-12 text-center">
            <i className="ri-qr-code-line text-6xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-500 mb-4">아직 생성된 QR 코드가 없습니다</p>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              첫 QR 코드 만들기
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {qrCodes.map((qr) => (
              <div key={qr.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 bg-white border-2 border-gray-200 rounded-lg p-2 flex-shrink-0">
                    <img src={qr.qr_image} alt={qr.name} className="w-full h-full object-contain" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">{qr.name}</h4>
                    <p className="text-sm text-gray-600 truncate mb-2">{qr.url}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-qr-scan-2-line"></i>
                        {qr.scans || 0} 스캔
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {new Date(qr.created_at).toLocaleDateString('ko-KR')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAnalyticsModal({ isOpen: true, qrCode: qr })}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-bar-chart-line mr-2"></i>
                      분석
                    </button>
                    <button
                      onClick={() => handleDownload(qr.qr_image, qr.name)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                      title="다운로드"
                    >
                      <i className="ri-download-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(qr.id)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap cursor-pointer"
                      title="삭제"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <QRCodeModal
          onClose={() => setShowModal(false)}
          onSuccess={loadQRCodes}
        />
      )}

      {analyticsModal.qrCode && (
        <AnalyticsModal
          isOpen={analyticsModal.isOpen}
          onClose={() => setAnalyticsModal({ isOpen: false, qrCode: null })}
          itemId={analyticsModal.qrCode.id}
          itemName={analyticsModal.qrCode.name}
          type="qr"
        />
      )}
    </>
  );
}
